import {useEffect, useRef, useState} from 'react'

/**
 * useIntersectionObserver hook
 *
 * @param element - React ref to the element to observe
 * @param options - IntersectionObserver options, with optional isOnce and threshold (number or {mobile, desktop})
 * @param shouldUseDesktopThreshold - Whether to use desktop threshold (default: true)
 * @returns { isIntersecting, observer }
 */
export function useIntersectionObserver(
  element: React.RefObject<Element | null>,
  options: Omit<IntersectionObserverInit, 'threshold'> & {
    isOnce?: boolean
    threshold?: number | {mobile?: number; desktop: number}
  } = {},
  shouldUseDesktopThreshold = true,
) {
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false)
  const observer = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (element?.current) {
      if (observer.current) {
        observer.current.unobserve(element.current)
        observer.current.disconnect()
      }

      observer.current = new IntersectionObserver(
        ([entry]) => {
          if (options.isOnce && entry?.isIntersecting) {
            observer.current?.disconnect()
          }

          setIsIntersecting(!!entry?.isIntersecting)
        },
        {
          ...options,
          threshold:
            typeof options.threshold === 'number'
              ? options.threshold
              : options.threshold?.[shouldUseDesktopThreshold ? 'desktop' : 'mobile'],
        },
      )

      observer.current.observe(element.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, shouldUseDesktopThreshold])

  useEffect(() => {
    const el = element?.current
    return () => {
      if (el && observer.current) {
        observer.current.unobserve(el)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isIntersecting,
    observer,
  }
}
